/**
 * UX Testing & External User Testing Schema
 * Testing participants, cohorts, scenarios, and insights
 */

import {
  pgTable,
  uuid,
  varchar,
  text,
  timestamp,
  integer,
  numeric,
  boolean,
} from 'drizzle-orm/pg-core';
import { relations } from 'drizzle-orm';

// =============================================================================
// Testing Participants
// =============================================================================
export const testingParticipants = pgTable('testing_participants', {
  id: uuid('id').primaryKey().defaultRandom(),
  participantId: varchar('participant_id', { length: 255 }).notNull().unique(),
  cohortId: uuid('cohort_id'),
  email: varchar('email', { length: 255 }),
  status: varchar('status', { length: 50 }).notNull().default('active'), // 'active', 'completed', 'abandoned'
  accepted: boolean('accepted').default(false),
  prescreeningScore: numeric('prescreening_score'),
  sessionData: text('session_data'), // JSON
  performanceMetrics: text('performance_metrics'), // JSON
  completedAt: timestamp('completed_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Testing Cohorts
// =============================================================================
export const testingCohorts = pgTable('testing_cohorts', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  variant: varchar('variant', { length: 50 }).notNull(), // 'control', 'variant_a', 'variant_b', etc.
  description: text('description'),
  configuration: text('configuration'), // JSON
  isActive: boolean('is_active').default(true),
  startDate: timestamp('start_date'),
  endDate: timestamp('end_date'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Scenario Executions
// =============================================================================
export const scenarioExecutions = pgTable('scenario_executions', {
  id: uuid('id').primaryKey().defaultRandom(),
  participantId: varchar('participant_id', { length: 255 }).notNull(),
  cohortId: uuid('cohort_id'),
  scenarioId: varchar('scenario_id', { length: 255 }).notNull(),
  success: boolean('success'),
  successScore: numeric('success_score'),
  durationMs: integer('duration_ms'),
  startedAt: timestamp('started_at').defaultNow().notNull(),
  completedAt: timestamp('completed_at'),
  abandonmentPoint: varchar('abandonment_point', { length: 255 }),
  errors: text('errors'), // JSON array
  metadata: text('metadata'), // JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// Testing Insights
// =============================================================================
export const testingInsights = pgTable('testing_insights', {
  id: uuid('id').primaryKey().defaultRandom(),
  insightType: varchar('insight_type', { length: 50 }).notNull(), // 'success', 'warning', 'critical'
  category: varchar('category', { length: 100 }).notNull(),
  title: varchar('title', { length: 255 }).notNull(),
  description: text('description'),
  metrics: text('metrics'), // JSON
  recommendations: text('recommendations'), // JSON array
  priority: integer('priority').default(5),
  status: varchar('status', { length: 50 }).default('new'), // 'new', 'acknowledged', 'resolved'
  resolvedAt: timestamp('resolved_at'),
  resolvedBy: uuid('resolved_by'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// UX Testing Events
// =============================================================================
export const uxTestingEvents = pgTable('ux_testing_events', {
  id: uuid('id').primaryKey().defaultRandom(),
  sessionId: varchar('session_id', { length: 255 }).notNull(),
  userId: varchar('user_id', { length: 255 }).notNull(),
  eventType: varchar('event_type', { length: 100 }).notNull(),
  metadata: text('metadata'), // JSON
  context: text('context'), // JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// UX Testing Sessions
// =============================================================================
export const uxTestingSessions = pgTable('ux_testing_sessions', {
  id: uuid('id').primaryKey().defaultRandom(),
  sessionId: varchar('session_id', { length: 255 }).notNull().unique(),
  userId: varchar('user_id', { length: 255 }).notNull(),
  startTime: timestamp('start_time').defaultNow().notNull(),
  endTime: timestamp('end_time'),
  duration: integer('duration'),
  eventsRecorded: integer('events_recorded').default(0),
  experimentsActive: text('experiments_active'), // JSON array
  feedbackProvided: integer('feedback_provided').default(0),
  conversionOutcome: varchar('conversion_outcome', { length: 50 }), // 'converted', 'churned', 'active'
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// User Feedback
// =============================================================================
export const userFeedback = pgTable('user_feedback', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: varchar('user_id', { length: 255 }).notNull(),
  sessionId: varchar('session_id', { length: 255 }),
  feedbackType: varchar('feedback_type', { length: 50 }).notNull(), // 'nps', 'feature_request', 'bug_report', 'general', 'exit_survey'
  rating: integer('rating'), // 0-10 for NPS, 1-5 for satisfaction
  comment: text('comment'),
  metadata: text('metadata'), // JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// Relations
// =============================================================================
export const testingParticipantsRelations = relations(testingParticipants, ({ one, many }) => ({
  cohort: one(testingCohorts, {
    fields: [testingParticipants.cohortId],
    references: [testingCohorts.id],
  }),
  executions: many(scenarioExecutions),
}));

export const testingCohortsRelations = relations(testingCohorts, ({ many }) => ({
  participants: many(testingParticipants),
  executions: many(scenarioExecutions),
}));

export const scenarioExecutionsRelations = relations(scenarioExecutions, ({ one }) => ({
  cohort: one(testingCohorts, {
    fields: [scenarioExecutions.cohortId],
    references: [testingCohorts.id],
  }),
}));

// =============================================================================
// Email Jobs
// =============================================================================
export const emailJobs = pgTable('email_jobs', {
  id: varchar('id', { length: 255 }).primaryKey(),
  userId: varchar('user_id', { length: 255 }).notNull(),
  templateId: varchar('template_id', { length: 100 }).notNull(),
  scheduledFor: timestamp('scheduled_for').notNull(),
  status: varchar('status', { length: 50 }).notNull().default('pending'), // 'pending', 'sent', 'failed', 'cancelled'
  sentAt: timestamp('sent_at'),
  error: text('error'),
  metadata: text('metadata'), // JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Types
// =============================================================================
export type TestingParticipant = typeof testingParticipants.$inferSelect;
export type NewTestingParticipant = typeof testingParticipants.$inferInsert;
export type TestingCohort = typeof testingCohorts.$inferSelect;
export type NewTestingCohort = typeof testingCohorts.$inferInsert;
export type ScenarioExecution = typeof scenarioExecutions.$inferSelect;
export type NewScenarioExecution = typeof scenarioExecutions.$inferInsert;
export type TestingInsight = typeof testingInsights.$inferSelect;
export type NewTestingInsight = typeof testingInsights.$inferInsert;
export type UxTestingEvent = typeof uxTestingEvents.$inferSelect;
export type NewUxTestingEvent = typeof uxTestingEvents.$inferInsert;
export type UxTestingSession = typeof uxTestingSessions.$inferSelect;
export type NewUxTestingSession = typeof uxTestingSessions.$inferInsert;
export type UserFeedbackRecord = typeof userFeedback.$inferSelect;
export type NewUserFeedbackRecord = typeof userFeedback.$inferInsert;
export type EmailJob = typeof emailJobs.$inferSelect;
export type NewEmailJob = typeof emailJobs.$inferInsert;
// Note: ErrorLog types are exported from analytics.ts
